import React, { useEffect, useRef, useState } from 'react'
import AdminPageTitle from '../../components/AdminPageTitle/AdminPageTitle'
import { Button, Select, Form, Typography, message, Card, Space, Input, Tag, Spin, Alert, Steps, Divider, Descriptions, Progress, Table } from 'antd'
import { OrderedListOutlined, FileTextOutlined, EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { Layer, LayerAttributeDictionary } from '../../services/models'
import objImportsService, { GetStepName, GetStepColor, ObjImport, ObjImportLog, GetObjImportLogStatusName, GetObjImportLogStatusColor } from '../../services/objImportsService'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Helmet } from 'react-helmet-async'

type Props = {}

const AdminImportPage: React.FC<Props> = () => {
  const navigate = useNavigate()

  const { id } = useParams()
  if (id == null) {
    return <></>
  }
  const [data, setData] = useState<{
    id: number,
    objImport: ObjImport,
    layer: Layer,
    headers: string[],
    values: { [key: number]: { value: string, id: number | null } [] }
  }>()

  const [fetching, setFetching] = useState(false)
  const [savingHeaders, setSavingHeaders] = useState(false)
  const [savingValues, setSavingValues] = useState(false)
  const [starting, setStarting] = useState(false)
  const [logs, setLogs] = useState<ObjImportLog[]>([])
  const [logsTotal, setLogsTotal] = useState(0)
  const [logsPage, setLogsPage] = useState(1)
  const [logsPageSize, setLogsPageSize] = useState(50)
  const fetchingRef = useRef(false)

  const [formHeaders] = Form.useForm()
  const [formValues] = Form.useForm()

  const fetchLogs = async (page = logsPage, pageSize = logsPageSize) => {
    try {
      const logsResponse = await objImportsService.getLogs(+id, pageSize, (page - 1) * pageSize)
      setLogs(logsResponse.logs)
      setLogsTotal(logsResponse.total)
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    }
  }

  const fetchData = async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    setFetching(true)
    try {
      const importResponse = await objImportsService.get(+id)

      for (const [key, value] of Object.entries(importResponse.objImport.mapHeaders)) {
        formHeaders.setFieldValue(key, value)
      }

      for (const [attributeId, values] of Object.entries(importResponse.values)) {
        values.forEach(v => {
          formValues.setFieldValue([attributeId, v.value], v.id)
        })
      }

      for (const [attributeId, values] of Object.entries(importResponse.objImport.mapValues)) {
        for (const [value, valueId] of Object.entries(values)) {
          const v = formValues.getFieldValue([attributeId, value])
          if (v === 0) {
            formValues.setFieldValue([attributeId, value], valueId)
          }
        }
      }

      setData({
        objImport: importResponse.objImport,
        id: importResponse.id,
        layer: importResponse.layer,
        headers: importResponse.headers,
        values: importResponse.values
      })

      await fetchLogs()
    } catch (error) {
      console.error('Failed to fetch data:', error)
    } finally {
      fetchingRef.current = false
      setFetching(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (data?.objImport.step !== 4 && data?.objImport.step !== 5) return
    const interval = setInterval(() => {
      fetchData()
    }, 3000)
    return () => clearInterval(interval)
  }, [data?.objImport.step])

  const getWizardStep = (step: number) => {
    if (step === 1) return 0
    if (step === 2) return 1
    return 2
  }

  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (data) {
      setActiveStep(getWizardStep(data.objImport.step))
    }
  }, [data?.objImport.step])

  const renderSelectAttributes = () => {
    if (!data) return <></>
    return (
      <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        defaultValue={null}
        options={[
          { label: 'Пропустить', value: null },
          { label: 'Название', value: 'global:name' },
          { label: 'Описание', value: 'global:description' },
          { label: 'Lat', value: 'global:lat' },
          { label: 'Long', value: 'global:long' },
          ...data.layer.attributeNumbers.map((a) => ({
            label: a.name,
            value: `number:${a.id}`
          })),
          ...data.layer.attributeTexts.map((a) => ({
            label: a.name,
            value: `text:${a.id}`
          })),
          ...data.layer.attributeDictionaries.map((a) => ({
            label: a.name,
            value: `dictionary:${a.id}`
          }))
        ]}
      />
    )
  }

  const renderValue = (attribute: LayerAttributeDictionary) => {
    if (!data) return null
    if (!data.values[attribute.id]) {
      return null
    }

    const values = data.values[attribute.id]

    return (
      <div>
        {values.map((v, i) => (
          <Form.Item label={v.value} key={`${i}`} name={[attribute.id, v.value]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={[
                { value: 0, label: 'Создать' },
                ...attribute.values.map(av => ({ value: av.id, label: av.name }))
              ]}
            />
          </Form.Item>
        ))}
      </div>
    )
  }

  if (!data && fetching) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />
  }

  if (!data) return <></>

  const formsDisabled = data.objImport.step === 4 || data.objImport.step === 5

  return (
    <Card>
      <Helmet><title>Prisma — Сопоставление</title></Helmet>
      <AdminPageTitle
        title={'Сопоставление'}
        buttonText={'Список'}
        icon={<OrderedListOutlined/>}
        onClickButton={() => navigate('/import')}
      />
      <Spin spinning={fetching}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <FileTextOutlined/>
                <Typography.Text strong style={{ fontSize: 16 }}>{data.objImport.filename}</Typography.Text>
              </Space>
              <Tag color={GetStepColor(data.objImport.step)} style={{ padding: '2px 12px', fontSize: 14 }}>
                {GetStepName(data.objImport.step)}
              </Tag>
            </div>
            <Space style={{ marginBottom: 8 }}>
              <span><EnvironmentOutlined style={{ marginRight: 4 }}/> {data.layer.name}</span>
              <span><ClockCircleOutlined style={{ marginRight: 4 }}/> {dayjs(data.objImport.createdAt).format('YYYY.MM.DD HH:mm')}</span>
            </Space>
            <Typography.Text strong style={{ marginBottom: 4, display: 'block' }}>Описание</Typography.Text>
            <Input
              defaultValue={data.objImport.description}
              onBlur={(e) => {
                objImportsService.updateDescription(data!.id, e.target.value).then(() => {
                  message.success('Описание успешно обновлено!')
                }).catch(() => {
                  message.success('Описание не обновлено!')
                })
              }}
              placeholder="Описание"
            />
            <Divider style={{ margin: '0 0 12px' }}/>
          <Steps
            current={activeStep}
            onChange={(s) => {
              const step = data.objImport.step
              if (s === 0) setActiveStep(0)
              else if (s === 1 && step >= 2) setActiveStep(1)
              else if (s === 2 && step >= 3) setActiveStep(2)
            }}
            items={[
              { title: 'Колонки', description: 'Сопоставление столбцов CSV' },
              { title: 'Значения', description: 'Сопоставление значений словарей' },
              { title: 'Импорт', description: data.objImport.step >= 4 ? GetStepName(data.objImport.step) : 'Запуск' }
            ]}
            style={{ marginBottom: 24 }}
          />

          {activeStep === 0 && (
            <Form
              disabled={formsDisabled}
              labelCol={{ span: 6 }}
              layout="horizontal"
              form={formHeaders}
              onFinish={(d) => {
                setSavingHeaders(true)
                objImportsService.updateMapHeaders(data.id, d).then(() => fetchData()).finally(() => setSavingHeaders(false))
              }}>
              {data.headers.map((h, i) => (
                <Form.Item label={h} name={'header:' + i} key={'header-' + i}
                           rules={[
                             {
                               validator: (_, value) => {
                                 const formValues = formHeaders.getFieldsValue()
                                 const currentValues = Object.values(formValues)

                                 if (value !== null && value !== undefined) {
                                   const duplicateCount = currentValues.filter(v => v === value).length
                                   if (duplicateCount > 1) {
                                     return Promise.reject(new Error('Значение должно быть уникальным'))
                                   }
                                 }
                                 return Promise.resolve()
                               }
                             }
                           ]}
                >
                  {renderSelectAttributes()}
                </Form.Item>
              ))}

              <Button type="primary" htmlType="submit" loading={savingHeaders}>
                Сохранить
              </Button>
            </Form>
          )}

          {activeStep === 1 && (
            <Form
              disabled={formsDisabled}
              labelCol={{ span: 6 }}
              layout="horizontal"
              form={formValues}
              onFinish={(d) => {
                setSavingValues(true)
                objImportsService.updateMapValues(data.id, d).then(() => fetchData()).finally(() => setSavingValues(false))
              }}
            >
              {data.layer.attributeDictionaries.map(a => (
                <div key={a.id.toString()}>
                  <Typography.Title level={5}>{a.name}</Typography.Title>
                  {renderValue(a)}
                </div>
              ))}
              <Button type="primary" htmlType="submit" loading={savingValues}>
                Сохранить
              </Button>
            </Form>
          )}

          {activeStep === 2 && (
            <>
              {data.objImport.step === 7 && (
                <Alert
                  type="error"
                  message="Ошибка импорта"
                  description={data.objImport.result?.errorMessage || 'Неизвестная ошибка'}
                  style={{ marginBottom: 16 }}
                  closable
                />
              )}
              {(data.objImport.step === 4 || data.objImport.step === 5) && data.objImport.result && data.objImport.result.total > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Progress
                    percent={Math.round((data.objImport.result.processed || 0) / data.objImport.result.total * 100)}
                    status="active"
                  />
                  <Typography.Text type="secondary">
                    Обработано {data.objImport.result.processed || 0} из {data.objImport.result.total}
                  </Typography.Text>
                </div>
              )}
              {data.objImport.result && data.objImport.result.create !== undefined && (
                <Descriptions style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Создано">{data.objImport.result.create}</Descriptions.Item>
                  <Descriptions.Item label="Обновлено">{data.objImport.result.update}</Descriptions.Item>
                  <Descriptions.Item label="Пропущено">{data.objImport.result.skip ?? 0}</Descriptions.Item>
                  <Descriptions.Item label="Ошибок">{data.objImport.result.error}</Descriptions.Item>
                </Descriptions>
              )}
              <Button
                disabled={!(data.objImport.step === 3 || data.objImport.step === 6 || data.objImport.step === 7) || starting}
                type="primary"
                size="large"
                loading={starting}
                onClick={() => {
                  setStarting(true)
                  objImportsService.start(data.id).then((res) => {
                    if (res.status === 'ok') {
                      message.success('Импорт запущен')
                      fetchData()
                    } else {
                      message.error('Что-то пошло не так')
                    }
                  }).catch(() => {
                    message.error('Ошибка при запуске импорта')
                  }).finally(() => setStarting(false))
                }}
              >
                {data.objImport.step === 6 ? 'Перезапустить импорт' : data.objImport.step === 7 ? 'Повторить импорт' : 'Начать'}
              </Button>
              {logs.length > 0 && (
                <>
                  <Divider style={{ marginTop: 24 }}>Логи импорта</Divider>
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={logs}
                    pagination={{
                      current: logsPage,
                      pageSize: logsPageSize,
                      total: logsTotal,
                      showSizeChanger: true,
                      pageSizeOptions: [20, 50, 100],
                      onChange: (page, pageSize) => {
                        setLogsPage(page)
                        setLogsPageSize(pageSize)
                        fetchLogs(page, pageSize)
                      }
                    }}
                    scroll={{ y: 360 }}
                    columns={[
                      {
                        title: 'Строка',
                        dataIndex: 'rowNumber',
                        width: 80,
                        render: (v) => v ?? '—'
                      },
                      {
                        title: 'Статус',
                        dataIndex: 'status',
                        width: 120,
                        render: (v) => <Tag color={GetObjImportLogStatusColor(v)}>{GetObjImportLogStatusName(v)}</Tag>
                      },
                      {
                        title: 'Сообщение',
                        dataIndex: 'message'
                      },
                      {
                        title: 'Время',
                        dataIndex: 'createdAt',
                        width: 170,
                        render: (v) => dayjs(v).format('YYYY.MM.DD HH:mm:ss')
                      }
                    ]}
                  />
                </>
              )}
            </>
          )}
        </Space>
      </Spin>
    </Card>
  )
}

export default AdminImportPage
